// Téléchargement côté navigateur.
//
// Le CLI délègue à yt-dlp / ffmpeg / aria2c. Ici il n'y a pas de processus à
// lancer : on récupère les segments à travers le proxy et on les assemble dans
// un Blob. Un flux HLS en MPEG-TS donne un fichier .ts — lisible tel quel par
// mpv ou VLC, et remuxable en MP4 par `ffmpeg -i x.ts -c copy x.mp4`. Un flux
// en fMP4 (#EXT-X-MAP) et les fichiers progressifs donnent directement un .mp4.

export interface Progress {
  /** Segments terminés, 0 quand la taille totale est inconnue. */
  done: number;
  total: number;
  bytes: number;
}

export interface DownloadResult {
  blob: Blob;
  filename: string;
}

const PARALLEL = 6;

interface Key {
  bytes: ArrayBuffer;
  iv: Uint8Array<ArrayBuffer> | null;
}

interface Segment {
  url: string;
  key: Key | null;
  sequence: number;
}

const ATTR_RE = /([A-Z0-9-]+)=("[^"]*"|[^,]*)/g;

function attributes(line: string): Map<string, string> {
  const found = new Map<string, string>();

  for (const match of line.slice(line.indexOf(":") + 1).matchAll(ATTR_RE)) {
    found.set(match[1]!, match[2]!.replace(/^"|"$/g, ""));
  }

  return found;
}

function hexToBytes(hex: string): Uint8Array<ArrayBuffer> {
  const clean = hex.replace(/^0[xX]/, "");
  const bytes = new Uint8Array(clean.length / 2);

  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = Number.parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  }

  return bytes;
}

/** IV par défaut d'un segment AES-128 : son numéro de séquence, sur 16 octets
 *  en big-endian. */
function sequenceIV(sequence: number): Uint8Array<ArrayBuffer> {
  const iv = new Uint8Array(16);
  new DataView(iv.buffer).setUint32(12, sequence);

  return iv;
}

async function loadKey(url: string, ivAttr: string | undefined): Promise<Key> {
  const res = await fetch(url);

  if (!res.ok) throw new Error("clé de déchiffrement inaccessible");

  return {
    bytes: await res.arrayBuffer(),
    iv: ivAttr ? hexToBytes(ivAttr) : null,
  };
}

/** Analyse une playlist de segments déjà réécrite par le proxy : toutes les URI
 *  y sont des chemins /p?u=…, il n'y a donc rien à résoudre. */
async function parseMediaPlaylist(
  body: string,
  signal: AbortSignal,
): Promise<{ segments: Segment[]; map: string | null }> {
  const segments: Segment[] = [];

  let key: Key | null = null;
  let map: string | null = null;
  let sequence = 0;

  for (const raw of body.split("\n")) {
    const line = raw.trim();
    if (line === "") continue;

    if (line.startsWith("#EXT-X-MEDIA-SEQUENCE")) {
      sequence = Number(line.split(":")[1] ?? 0) || 0;
      continue;
    }

    if (line.startsWith("#EXT-X-MAP")) {
      map = attributes(line).get("URI") ?? null;
      continue;
    }

    if (line.startsWith("#EXT-X-KEY")) {
      const attrs = attributes(line);
      const method = attrs.get("METHOD") ?? "NONE";

      if (method === "NONE") {
        key = null;
        continue;
      }

      if (method !== "AES-128") {
        throw new Error(`chiffrement non pris en charge : ${method}`);
      }

      // WebCrypto n'existe qu'en contexte sécurisé : sur une IP du réseau
      // local en HTTP, il est absent. La lecture, elle, continue de marcher —
      // hls.js déchiffre en JavaScript quand il n'a pas mieux.
      if (!globalThis.crypto?.subtle) {
        throw new Error(
          "flux chiffré : le déchiffrement exige un contexte sécurisé " +
            "(localhost ou HTTPS)",
        );
      }

      const uri = attrs.get("URI");
      if (!uri) throw new Error("clé de déchiffrement absente du manifeste");

      signal.throwIfAborted();
      key = await loadKey(uri, attrs.get("IV"));

      continue;
    }

    if (line.startsWith("#")) continue;

    segments.push({ url: line, key, sequence });
    sequence++;
  }

  return { segments, map };
}

async function fetchSegment(segment: Segment, signal: AbortSignal): Promise<Uint8Array> {
  const res = await fetch(segment.url, { signal });

  if (!res.ok) throw new Error(`segment indisponible (HTTP ${res.status})`);

  const data = await res.arrayBuffer();

  if (!segment.key) return new Uint8Array(data);

  const iv = segment.key.iv ?? sequenceIV(segment.sequence);

  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    segment.key.bytes,
    "AES-CBC",
    false,
    ["decrypt"],
  );

  const clear = await crypto.subtle.decrypt({ name: "AES-CBC", iv }, cryptoKey, data);

  return new Uint8Array(clear);
}

/** Télécharge les segments par petits paquets parallèles, en conservant leur
 *  ordre dans le fichier final. */
async function fetchSegments(
  segments: Segment[],
  onProgress: (progress: Progress) => void,
  signal: AbortSignal,
): Promise<Uint8Array[]> {
  const parts = new Array<Uint8Array>(segments.length);

  let next = 0;
  let done = 0;
  let bytes = 0;

  const worker = async (): Promise<void> => {
    while (next < segments.length) {
      const index = next++;

      signal.throwIfAborted();

      parts[index] = await fetchSegment(segments[index]!, signal);

      done++;
      bytes += parts[index]!.byteLength;

      onProgress({ done, total: segments.length, bytes });
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(PARALLEL, segments.length) }, worker),
  );

  return parts;
}

async function downloadProgressive(
  url: string,
  onProgress: (progress: Progress) => void,
  signal: AbortSignal,
): Promise<Blob> {
  const res = await fetch(url, { signal });

  if (!res.ok) throw new Error(`téléchargement impossible (HTTP ${res.status})`);
  if (!res.body) return await res.blob();

  const total = Number(res.headers.get("content-length") ?? "0");
  const reader = res.body.getReader();
  const parts: Uint8Array[] = [];

  let bytes = 0;

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;

    parts.push(value);
    bytes += value.byteLength;

    onProgress({ done: bytes, total, bytes });
  }

  return new Blob(parts as BlobPart[], { type: "video/mp4" });
}

export interface DownloadRequest {
  /** URL du flux, déjà passée par le proxy. Master HLS, playlist de segments,
   *  ou fichier progressif. */
  url: string;
  isHLS: boolean;
  name: string;
  onProgress: (progress: Progress) => void;
  signal: AbortSignal;
}

export async function download(request: DownloadRequest): Promise<DownloadResult> {
  const safeName = request.name.replace(/[\\/:*?"<>|]+/g, " ").trim() || "video";

  if (!request.isHLS) {
    const blob = await downloadProgressive(
      request.url,
      request.onProgress,
      request.signal,
    );

    return { blob, filename: `${safeName}.mp4` };
  }

  const res = await fetch(request.url, { signal: request.signal });

  if (!res.ok) throw new Error(`manifeste indisponible (HTTP ${res.status})`);

  const { segments, map } = await parseMediaPlaylist(await res.text(), request.signal);

  if (segments.length === 0) throw new Error("aucun segment dans ce manifeste");

  const parts = await fetchSegments(segments, request.onProgress, request.signal);

  // fMP4 : le segment d'initialisation doit précéder les autres, et le fichier
  // obtenu est un MP4 fragmenté, pas un flux de transport
  if (map) {
    const init = await fetch(map, { signal: request.signal });
    parts.unshift(new Uint8Array(await init.arrayBuffer()));
  }

  return {
    blob: new Blob(parts as BlobPart[], {
      type: map ? "video/mp4" : "video/mp2t",
    }),
    filename: `${safeName}.${map ? "mp4" : "ts"}`,
  };
}

export function save(result: DownloadResult): void {
  const url = URL.createObjectURL(result.blob);

  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = result.filename;
  anchor.click();

  // laisser au navigateur le temps de démarrer l'enregistrement
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
